/**
 * Ce que Consulter lit dans la base, avec les droits de l'administration :
 * la recherche, les semaines des centres, les enfants d'une semaine et leurs
 * fiches, les photos, l'historique des inscriptions d'un enfant.
 *
 * Les fiches sont remises dans le modèle du parent (`depuisLignes`) : la
 * liste, « À savoir » et la fiche disent la même chose que ce que le parent
 * a rempli.
 */

import { db } from '../../../../lib/payments/db'
import { sansJpa, stagesDesJpa } from '../../../../lib/jpa/stages.supabase'
import { lireClassesDesEnfants } from '../../../../lib/jpa/gateway.supabase'
import { estVivante, type SessionDuCentre } from '../../../../lib/presences/session'
import { depuisLignes, type Fiche, type LignesLues } from '../../../../components/forms/kid/fiche'
import type { ClasseDuCentre } from '../../../../lib/classes/regles'
import { comparerResultats, correspond, motifIlike, type EnfantDeLaSemaine, type PersonneConnue, type Recherche } from './regles'

const PAR_LOT = 200

/** `select … in (…)` par lots : l'adresse d'une requête a une longueur maximale. */
async function parLots<T>(lire: (ids: string[]) => PromiseLike<{ data: unknown; error: unknown }>, ids: readonly string[]): Promise<T[]> {
  const uniques = [...new Set(ids)]
  const tout: T[] = []
  for (let i = 0; i < uniques.length; i += PAR_LOT) {
    const { data, error } = await lire(uniques.slice(i, i + PAR_LOT))
    if (error) throw error
    tout.push(...((data ?? []) as T[]))
  }
  return tout
}

const un = <T,>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null)

/* ------------------------------------------------------------------------ */
/*  La recherche                                                             */
/* ------------------------------------------------------------------------ */

export interface EnfantTrouve {
  kidId: string
  prenom: string
  nom: string
  dateNaissance: string | null
  ecole: string | null
  photo: string | null
  parent: { prenom: string | null; nom: string | null; email: string | null } | null
}

interface KidLu {
  id: string
  prenom: string | null
  nom: string | null
  date_naissance: string | null
  ecole: string | null
  photo_url: string | null
  users: { prenom: string | null; nom: string | null; email: string | null } | { prenom: string | null; nom: string | null; email: string | null }[] | null
}

const SELECTION_TROUVE = 'id, prenom, nom, date_naissance, ecole, photo_url, users!kids_user_id_fkey(prenom, nom, email)'

const versTrouve = (k: KidLu): EnfantTrouve => ({
  kidId: k.id,
  prenom: k.prenom ?? '',
  nom: k.nom ?? '',
  dateNaissance: k.date_naissance,
  ecole: k.ecole,
  photo: k.photo_url,
  parent: un(k.users),
})

/**
 * Les enfants qui répondent à la recherche, cinquante au plus. Deux lectures :
 * les enfants dont le nom contient chaque mot, et ceux dont le parent le
 * contient ; puis le tri sans accents (`correspond`) et l'ordre des résultats.
 */
export async function rechercherEnfants(r: Recherche): Promise<EnfantTrouve[]> {
  if (r.type === 'aucune') return []

  const parentsDe = async (requete: PromiseLike<{ data: unknown; error: unknown }>): Promise<KidLu[]> => {
    const { data, error } = await requete
    if (error) throw error
    const ids = ((data ?? []) as { id: string }[]).map(u => u.id)
    if (ids.length === 0) return []
    return parLots<KidLu>(lot => db.from('kids').select(SELECTION_TROUVE).in('user_id', lot).limit(200), ids)
  }

  if (r.type === 'email') {
    const kids = await parentsDe(db.from('users').select('id').ilike('email', `%${r.texte}%`).limit(50))
    return kids.map(versTrouve).sort(comparerResultats([])).slice(0, 50)
  }

  let enfants = db.from('kids').select(SELECTION_TROUVE)
  let parents = db.from('users').select('id')
  for (const mot of r.mots) {
    const m = motifIlike(mot)
    enfants = enfants.or(`prenom.ilike.${m},nom.ilike.${m}`)
    parents = parents.or(`prenom.ilike.${m},nom.ilike.${m},email.ilike.${m}`)
  }
  const [parNom, parParent] = await Promise.all([
    (async () => {
      const { data, error } = await enfants.order('prenom').limit(200)
      if (error) throw error
      return (data ?? []) as KidLu[]
    })(),
    parentsDe(parents.limit(100)),
  ])

  const vus = new Map<string, EnfantTrouve>()
  for (const k of [...parNom, ...parParent]) {
    const e = versTrouve(k)
    if (vus.has(e.kidId)) continue
    if (!correspond([e.prenom, e.nom, e.parent?.prenom, e.parent?.nom, e.parent?.email], r.mots)) continue
    vus.set(e.kidId, e)
  }
  return [...vus.values()].sort(comparerResultats(r.mots)).slice(0, 50)
}

/* ------------------------------------------------------------------------ */
/*  Les semaines                                                             */
/* ------------------------------------------------------------------------ */

export interface CentreConsulte {
  id: string
  nom: string
}

export interface SessionConsultee extends SessionDuCentre {
  centreId: string
  centre: string
}

interface SessionLue {
  id: string
  center_id: string
  stage_id: string
  start_date: string
  end_date: string
  periode: string
  semaine: string | null
  stages: { title: string } | { title: string }[] | null
  centers: { name: string } | { name: string }[] | null
}

/** Les centres de vacances actifs et toutes leurs sessions (sans les journées pédagogiques). */
export async function lireLesSessions(): Promise<{ centres: CentreConsulte[]; sessions: SessionConsultee[] }> {
  const [centresRes, sessionsRes] = await Promise.all([
    db.from('centers').select('id, name').eq('active', true).eq('archived', false).eq('centre_de_vacances', true).order('name'),
    sansJpa(
      db.from('sessions')
        .select('id, center_id, stage_id, start_date, end_date, periode, semaine, stages(title), centers(name)')
        .eq('active', true)
        .eq('archived', false)
        .order('start_date'),
      await stagesDesJpa(),
    ),
  ])
  if (centresRes.error) throw centresRes.error
  if (sessionsRes.error) throw sessionsRes.error
  return {
    centres: ((centresRes.data ?? []) as { id: string; name: string }[]).map(c => ({ id: c.id, nom: c.name })),
    sessions: ((sessionsRes.data ?? []) as unknown as SessionLue[]).map(s => ({
      id: s.id,
      stageId: s.stage_id,
      stage: un(s.stages)?.title ?? '',
      debut: s.start_date,
      fin: s.end_date,
      periode: s.periode,
      semaine: s.semaine,
      centreId: s.center_id,
      centre: un(s.centers)?.name ?? '',
    })),
  }
}

interface InscriptionLue {
  id: string
  activity_id: string
  kid_id: string
  cancellation_status: string | null
  subgroup_name: string | null
  kids: { prenom: string | null; nom: string | null; date_naissance: string | null } | { prenom: string | null; nom: string | null; date_naissance: string | null }[] | null
  subgroups: { name: string } | { name: string }[] | null
}

/** Les enfants inscrits (inscription vivante) aux sessions de la semaine. */
export async function lireLaSemaine(sessions: readonly SessionConsultee[]): Promise<EnfantDeLaSemaine[]> {
  const parId = new Map(sessions.map(s => [s.id, s]))
  const lues = await parLots<InscriptionLue>(
    ids => db.from('registrations')
      .select('id, activity_id, kid_id, cancellation_status, subgroup_name, kids(prenom, nom, date_naissance), subgroups(name)')
      .in('activity_id', ids),
    sessions.map(s => s.id),
  )
  return lues
    .filter(i => estVivante(i.cancellation_status))
    .map(i => {
      const k = un(i.kids)
      const s = parId.get(i.activity_id)
      return {
        registrationId: i.id,
        kidId: i.kid_id,
        prenom: k?.prenom ?? '',
        nom: k?.nom ?? '',
        dateNaissance: k?.date_naissance ?? null,
        centre: s?.centre ?? '',
        stage: s?.stage ?? '',
        sousGroupe: un(i.subgroups)?.name ?? i.subgroup_name ?? null,
      }
    })
}

/* ------------------------------------------------------------------------ */
/*  Les fiches de plusieurs enfants                                          */
/* ------------------------------------------------------------------------ */

export interface FichesLues {
  fiches: Map<string, Fiche>
  /** La ligne `kids` : photo, école, date de vérification. */
  kids: Map<string, { photo_url: string | null; ecole: string | null; last_verified_at: string | null }>
  /** Les personnes autorisées que les fiches désignent. */
  personnes: PersonneConnue[]
}

/** Les fiches de ces enfants, dans le modèle du parent. */
export async function lireLesFiches(kidIds: readonly string[]): Promise<FichesLues> {
  const parEnfant = (table: string) => parLots<{ kid_id: string }>(ids => db.from(table).select('*').in('kid_id', ids), kidIds)
  const [kids, sante, allergies, activites, departs, inclusions, images] = await Promise.all([
    parLots<Record<string, unknown> & { id: string }>(ids => db.from('kids').select('*').in('id', ids), kidIds),
    parEnfant('kid_health'),
    parEnfant('kid_allergies'),
    parEnfant('kid_activities'),
    parEnfant('kid_departure'),
    parEnfant('kid_inclusion'),
    parEnfant('kid_image_rights'),
  ])
  const index = (lignes: { kid_id: string }[]) => new Map(lignes.map(l => [l.kid_id, l]))
  const [s, a, ac, d, inc, img] = [sante, allergies, activites, departs, inclusions, images].map(index)

  const personneIds = departs.flatMap(x => ((x as { pickup_people_ids?: string[] | null }).pickup_people_ids ?? []))
  const personnes = await parLots<PersonneConnue>(
    ids => db.from('authorized_persons').select('id, first_name, last_name').in('id', ids),
    personneIds,
  )

  const fiches = new Map<string, Fiche>()
  const lignesKids = new Map<string, { photo_url: string | null; ecole: string | null; last_verified_at: string | null }>()
  for (const k of kids) {
    const lues = {
      kids: k,
      health: s.get(k.id),
      allergies: a.get(k.id),
      activities: ac.get(k.id),
      departure: d.get(k.id),
      inclusion: inc.get(k.id),
      image_rights: img.get(k.id),
    } as LignesLues
    fiches.set(k.id, depuisLignes(lues))
    lignesKids.set(k.id, {
      photo_url: (k.photo_url as string | null) ?? null,
      ecole: (k.ecole as string | null) ?? null,
      last_verified_at: (k.last_verified_at as string | null) ?? null,
    })
  }
  return { fiches, kids: lignesKids, personnes }
}

/* ------------------------------------------------------------------------ */
/*  Les écoles, les classes, les photos                                      */
/* ------------------------------------------------------------------------ */

/** Le nom de chaque école de la liste, par identifiant. */
export async function lireLesEcoles(): Promise<Map<string, string>> {
  const { data, error } = await db.from('schools').select('id, name')
  if (error) throw error
  return new Map(((data ?? []) as { id: string; name: string }[]).map(e => [e.id, e.name]))
}

export async function lireLesClasses(kidIds: readonly string[], annee: number): Promise<Map<string, ClasseDuCentre>> {
  const tout = new Map<string, ClasseDuCentre>()
  const uniques = [...new Set(kidIds)]
  for (let i = 0; i < uniques.length; i += PAR_LOT) {
    for (const [k, c] of await lireClassesDesEnfants(uniques.slice(i, i + PAR_LOT), annee)) tout.set(k, c)
  }
  return tout
}

/** Les adresses signées des photos (une heure), par chemin. */
export async function signerLesPhotos(chemins: readonly (string | null | undefined)[]): Promise<Map<string, string>> {
  const uniques = [...new Set(chemins.filter((c): c is string => !!c && !!c.trim()))]
  const urls = new Map<string, string>()
  for (let i = 0; i < uniques.length; i += PAR_LOT) {
    const lot = uniques.slice(i, i + PAR_LOT)
    const { data, error } = await db.storage.from('kid-photos').createSignedUrls(lot, 3600)
    if (error) throw error
    ;(data ?? []).forEach((s, j) => {
      if (s.signedUrl && !s.error) urls.set(lot[j], s.signedUrl)
    })
  }
  return urls
}

/* ------------------------------------------------------------------------ */
/*  Les inscriptions d'un enfant                                             */
/* ------------------------------------------------------------------------ */

export interface InscriptionDeLEnfant {
  id: string
  stage: string
  centre: string
  debut: string | null
  fin: string | null
  sousGroupe: string | null
  annulee: boolean
}

interface InscriptionHistoriqueLue {
  id: string
  cancellation_status: string | null
  subgroup_name: string | null
  session: {
    start_date: string | null
    end_date: string | null
    stage: { title: string } | { title: string }[] | null
    center: { name: string } | { name: string }[] | null
  } | { start_date: string | null; end_date: string | null; stage: unknown; center: unknown }[] | null
}

/** Les inscriptions de l'enfant, la plus récente d'abord. */
export async function lireLesInscriptions(kidId: string): Promise<InscriptionDeLEnfant[]> {
  const { data, error } = await db
    .from('registrations')
    .select('id, cancellation_status, subgroup_name, session:activity_id(start_date, end_date, stage:stage_id(title), center:center_id(name))')
    .eq('kid_id', kidId)
  if (error) throw error
  return ((data ?? []) as InscriptionHistoriqueLue[])
    .map(r => {
      const s = un(r.session) as { start_date: string | null; end_date: string | null; stage: { title: string } | { title: string }[] | null; center: { name: string } | { name: string }[] | null } | null
      return {
        id: r.id,
        stage: un(s?.stage)?.title ?? '',
        centre: un(s?.center)?.name ?? '',
        debut: s?.start_date ?? null,
        fin: s?.end_date ?? null,
        sousGroupe: r.subgroup_name,
        annulee: !estVivante(r.cancellation_status),
      }
    })
    .sort((x, y) => (y.debut ?? '').localeCompare(x.debut ?? ''))
}
